import { html, nothing, unsafeCSS, type CSSResultGroup, type PropertyValues } from 'lit';
import { live } from 'lit/directives/live.js';
import { customElement, property, state } from 'lit/decorators.js';
import { LibElement, createUniqueId } from '../lib-element';
import '../icon/lib-icon';
import { LibComboBoxOption } from './lib-combobox-option';
import css from './lib-combobox.css';

@customElement('lib-combobox')
export class LibComboBox extends LibElement {
  static readonly formAssociated = true;
  private readonly internals = typeof this.attachInternals === 'function' ? this.attachInternals() : undefined;
  @property() name = '';
  @property({ attribute: 'input-id' }) inputId = '';
  @property({ type: Boolean }) autofocus = false;
  @property({ attribute: 'selected-form-value' }) selectedFormValue: string | null = null;
  @state() private formDisabled = false;
  private get effectivelyDisabled(): boolean { return this.disabled || this.formDisabled; }
  protected override get hostFocusSelector(): string { return this.searchable ? 'input' : '.lib-combobox-field'; }
  static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(css)];
  @state() private items: { value: string; text: string; formValue: string }[] = [];
  @property() value = '-1';
  private revision = 0;
  @property({ type: Boolean }) searchable = false;
  @property({ attribute: 'filter-mode' }) filterMode = 'startswith';
  @property({ attribute: 'no-results' }) noResults = '';
  @property({ attribute: 'selected-text' }) selectedText = '';
  @state() private query = '';
  @state() private editing = false;
  @property() headline = '';
  @property() placeholder = '';
  @property({ type: Boolean }) disabled = false;
  @property({ type: Boolean }) error = false;
  @property() message = '';
  @property() surface = 'white';
  @property({ attribute: 'aria-label' }) override ariaLabel: string | null = null;
  @state() private open = false;
  @state() private active = -1;
  @state() private up = false;
  @state() private maxHeight = 624;
  private readonly idPrefix = createUniqueId('lib-combobox');
  private frame = 0;
  private readonly resizeObserver = new ResizeObserver(() => this.schedulePosition());
  private readonly ancestorObserver = new MutationObserver(() => {
    if (this.open && !this.getClientRects().length) this.close();
    else this.schedulePosition();
  });
  private readonly schedulePosition = () => {
    if (!this.open || this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      if (!this.getClientRects().length) this.close();
      else this.positionList();
    });
  };

  private readonly optionsObserver = new MutationObserver(() => this.syncOptions());

  override connectedCallback(): void {
    super.connectedCallback();
    this.optionsObserver.observe(this, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['value', 'form-value'] });
    this.syncOptions();
  }

  private syncOptions(): void {
    const items = Array.from(this.children)
      .filter((child): child is LibComboBoxOption => child instanceof LibComboBoxOption)
      .map(option => ({ value: option.value, text: option.textContent ?? '', formValue: option.getAttribute('form-value') ?? option.value }));
    if (items.length === this.items.length && items.every((item, index) =>
      item.value === this.items[index].value && item.text === this.items[index].text && item.formValue === this.items[index].formValue)) return;
    this.items = items;
    this.syncFormValue();
    if (this.searchable && this.open) this.active = this.visible.length ? 0 : -1;
  }

  override disconnectedCallback(): void {
    this.optionsObserver.disconnect();
    this.stopPositioning();
    document.removeEventListener('pointerdown', this.outside, true);
    super.disconnectedCallback();
  }

  static override get observedAttributes(): string[] { return [...super.observedAttributes, 'server-state']; }

  override attributeChangedCallback(name: string, previous: string | null, value: string | null): void {
    if (name === 'server-state') {
      if (value !== null) {
        const state = JSON.parse(value) as { value: string; selectedText: string; selectedFormValue: string | null; revision: number };
        if (state.revision >= this.revision) {
          this.value = state.value;
          this.selectedText = state.selectedText;
          this.selectedFormValue = state.selectedFormValue;
        }
      }
      return;
    }
    if (this.hasAttribute('server-state') && ['value', 'selected-text', 'selected-form-value'].includes(name)) return;
    super.attributeChangedCallback(name, previous, value);
  }

  protected override willUpdate(changed: PropertyValues<this>): void {
    if (this.effectivelyDisabled) this.close();
    this.syncFormValue();
    if (changed.has('searchable') || (this.searchable && (changed.has('value') || changed.has('selectedText')))) {
      this.query = '';
      this.editing = false;
      if (this.open) this.close();
    }

  }

  protected override firstUpdated(): void {
    if (this.autofocus && !this.effectivelyDisabled)
      this.renderRoot.querySelector<HTMLElement>(this.searchable ? 'input' : 'button')?.focus();
  }

  /** Resets to the current value attribute, discarding uncommitted search text. */
  formResetCallback(): void {
    this.revision++;
    this.close();
    this.value = this.getAttribute('value') ?? '-1';
    this.syncFormValue();
    this.dispatchEvent(new CustomEvent('lib-combobox-reset', {
      bubbles: true, composed: true, detail: {
        value: this.value, revision: this.revision, hasSelection: this.items.some(item => item.value === this.value) || this.selectedFormValue !== null
      }
    }));
  }

  /** Applies native disabled-fieldset semantics. */
  formDisabledCallback(disabled: boolean): void {
    this.formDisabled = disabled;
    if (this.effectivelyDisabled) this.close();
    this.syncFormValue();
  }

  private syncFormValue(): void {
    const selected = this.items.find(item => item.value === this.value);
    const value = selected ? selected.formValue : this.selectedFormValue;
    this.internals?.setFormValue(this.effectivelyDisabled ? null : value);
  }

  private get visible() {
    const entries = this.items.map((item, index) => ({ item, index }));
    if (!this.searchable || !this.query) return entries;
    const normalize = (text: string) => text.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
    const query = normalize(this.query);
    return entries.filter(({ item }) => this.filterMode === 'contains'
      ? normalize(item.text).includes(query) : normalize(item.text).startsWith(query));
  }

  private get committedText(): string {
    return this.items.find(item => item.value === this.value)?.text ?? this.selectedText;
  }

  override updated(): void {
    if (this.open && (this.effectivelyDisabled || (!this.searchable && !this.items.length))) this.close();
    if (this.active >= this.visible.length) this.active = this.visible.length - 1;
    if (this.searchable && this.open) this.scrollActive();
    if (this.open) {
      const list = this.renderRoot.querySelector<HTMLElement>('ul');
      if (list && !list.matches(':popover-open')) list.showPopover();
      this.positionList();
    }
  }

  private positionList(): void {
    const field = this.renderRoot.querySelector<HTMLElement>('.lib-combobox-field');
    const list = this.renderRoot.querySelector<HTMLElement>('ul');
    if (!this.open || !field || !list) return;
    const rect = field.getBoundingClientRect();
    const below = Math.max(0, innerHeight - rect.bottom);
    const above = Math.max(0, rect.top);
    const needed = Math.min(624, Math.max(1, this.visible.length) * 48);
    const up = below < needed && above > below;
    this.up = up;
    this.maxHeight = Math.max(0, Math.min(624, (up ? above : below) - 8));
    const width = Math.min(rect.width, Math.max(0, innerWidth - 16));
    list.style.width = `${width}px`;
    list.style.left = `${Math.max(8, Math.min(rect.left, innerWidth - width - 8))}px`;
    list.style.top = up ? 'auto' : `${rect.bottom}px`;
    list.style.bottom = up ? `${innerHeight - rect.top}px` : 'auto';
    list.style.maxHeight = `${this.maxHeight}px`;
  }

  private startPositioning(): void {
    document.addEventListener('scroll', this.schedulePosition, true);
    document.addEventListener('animationend', this.schedulePosition, true);
    window.addEventListener('resize', this.schedulePosition);
    this.resizeObserver.observe(this);
    const field = this.renderRoot.querySelector('.lib-combobox-field');
    if (field) this.resizeObserver.observe(field);
    for (let node = this.composedParent(this); node; node = this.composedParent(node)) {
      this.ancestorObserver.observe(node, { attributes: true, attributeFilter: ['class', 'style', 'open', 'hidden'] });
      this.resizeObserver.observe(node);
    }
  }

  private stopPositioning(): void {
    document.removeEventListener('scroll', this.schedulePosition, true);
    document.removeEventListener('animationend', this.schedulePosition, true);
    window.removeEventListener('resize', this.schedulePosition);
    this.resizeObserver.disconnect();
    this.ancestorObserver.disconnect();
    cancelAnimationFrame(this.frame);
    this.frame = 0;
  }

  private composedParent(node: Element): Element | null {
    const root = node.getRootNode();
    return node.assignedSlot ?? node.parentElement ?? (root instanceof ShadowRoot ? root.host : null);
  }

  private readonly outside = (event: PointerEvent) => {
    if (!event.composedPath().includes(this)) this.close();
  };

  private close(focus = false): void {
    const list = this.renderRoot?.querySelector<HTMLElement>('ul');
    if (list?.matches(':popover-open')) list.hidePopover();
    this.stopPositioning();
    this.open = false;
    this.active = -1;
    if (this.searchable) { this.query = ''; this.editing = false; }
    document.removeEventListener('pointerdown', this.outside, true);
    if (focus) this.renderRoot?.querySelector<HTMLElement>(this.searchable ? 'input' : 'button')?.focus({ preventScroll: true });
  }

  private async show(last = false): Promise<void> {
    if (this.effectivelyDisabled || (!this.searchable && !this.items.length) || this.open) return;
    const selected = this.visible.findIndex(({ item }) => item.value === this.value);
    let fallback = -1;
    if (this.visible.length) fallback = last ? this.visible.length - 1 : 0;
    this.active = selected >= 0 && selected < this.visible.length ? selected : fallback;
    this.open = true;
    this.startPositioning();
    document.addEventListener('pointerdown', this.outside, true);
    await this.updateComplete;
    if (!this.searchable) this.renderRoot.querySelector<HTMLElement>('ul')?.focus({ preventScroll: true });
    this.scrollActive();
  }

  private scrollActive(): void {
    const list = this.renderRoot.querySelector('ul');
    const option = list?.children[this.active] as HTMLElement | undefined;
    if (!list || !option) return;
    if (option.offsetTop < list.scrollTop) list.scrollTop = option.offsetTop;
    else if (option.offsetTop + option.offsetHeight > list.scrollTop + list.clientHeight)
      list.scrollTop = option.offsetTop + option.offsetHeight - list.clientHeight;
  }

  private select(index: number): void {
    if (this.effectivelyDisabled || index < 0 || index >= this.items.length) return;
    this.revision++;
    const value = this.items[index].value;
    const changed = this.value !== value;
    this.value = value;
    this.syncFormValue();
    this.close(true);
    this.dispatchEvent(new CustomEvent('lib-combobox-change', {
      detail: { value: this.value, revision: this.revision }, bubbles: true, composed: true,
    }));
    if (changed && !this.hasAttribute('server-state'))
      this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }

  private async openSearch(): Promise<void> {
    if (this.effectivelyDisabled) return;
    this.renderRoot.querySelector<HTMLInputElement>('input')?.focus({ preventScroll: true });
    await this.show();
  }

  private async input(event: Event): Promise<void> {
    event.stopPropagation();
    if (this.effectivelyDisabled) return;
    this.revision++;
    this.query = (event.target as HTMLInputElement).value;
    this.editing = true;
    await this.show();
    this.active = this.visible.length ? 0 : -1;
    await this.updateComplete;
    this.scrollActive();
  }

  private async searchKey(event: KeyboardEvent): Promise<void> {
    if (event.isComposing || event.key === 'Process' || event.ctrlKey || event.metaKey || event.altKey) return;
    const key = event.key;
    if (key === 'Escape' || key === 'Tab') {
      if (key === 'Escape' && this.open) event.preventDefault();
      this.close(); return;
    }
    if (!this.open) {
      if (['ArrowDown', 'ArrowUp', 'Enter'].includes(key)) {
        event.preventDefault();
        await this.show(key === 'ArrowUp');
      }
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter'].includes(key)) return;
    event.preventDefault();
    const visible = this.visible;
    if (key === 'Enter') {
      const entry = visible[this.active];
      if (entry) this.select(entry.index);
      return;
    }
    this.navigate(key, visible.length);
    await this.updateComplete;
    this.scrollActive();
  }

  private async key(event: KeyboardEvent): Promise<void> {
    if (this.effectivelyDisabled || event.isComposing || event.key === 'Process') return;
    if (this.searchable) { await this.searchKey(event); return; }
    const key = event.key;
    if (['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' ', 'Spacebar'].includes(key)) event.preventDefault();
    if (key === 'Escape') {
      if (this.open) event.preventDefault();
      this.close(true); return;
    }
    if (key === 'Tab') {
      this.closeForTab();
      return;
    }
    if (!this.open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' ', 'Spacebar'].includes(key)) await this.show(key === 'ArrowUp');
      return;
    }
    if (['Enter', ' ', 'Spacebar'].includes(key)) { this.select(this.active); return; }
    this.navigate(key, this.items.length);
    await this.updateComplete;
    this.scrollActive();
  }

  private navigate(key: string, count: number): void {
    switch (key) {
      case 'ArrowDown': this.active = Math.min(count - 1, this.active + 1); break;
      case 'ArrowUp': this.active = count || !this.searchable ? Math.max(0, this.active - 1) : -1; break;
      case 'Home': this.active = count || !this.searchable ? 0 : -1; break;
      case 'End': this.active = count - 1; break;
    }
  }

  private closeForTab(): void {
    // Keep native tab navigation anchored to the trigger when the focused list is removed.
    if (this.open) this.renderRoot.querySelector<HTMLElement>('button')?.focus({ preventScroll: true });
    this.close();
  }

  private get controlClasses(): string {
    return ['lib-combobox', this.open ? 'lib-combobox-open' : '', this.up && this.open ? 'lib-combobox-up' : '',
      this.effectivelyDisabled ? 'lib-combobox-disabled' : '', this.error ? 'lib-combobox-error' : '', this.surface === 'grey' ? 'lib-combobox-grey' : ''].join(' ');
  }

  private renderSelectionOnly() {
    const id = this.inputId || this.idPrefix, selected = this.items.findIndex(item => item.value === this.value);
    const listboxId = `${id}-listbox`;
    const messageId = `${id}-message`;
    const fieldLabel = this.headline ? `${id}-label` : nothing;
    const listLabel = this.headline ? `${id}-label` : id;
    const listStyle = `max-height: ${this.maxHeight}px`;
    const hasValue = selected >= 0 && selected < this.items.length;
    const labelId = `${id}-label`;
    const activeId = this.open && this.active >= 0 ? `${id}-option-${this.active}` : nothing;
    const valueClass = `lib-combobox-value${hasValue ? '' : ' lib-combobox-placeholder'}`;
    const options = this.items.map((item, index) => {
      const optionId = `${id}-option-${index}`;
      const classes = `lib-combobox-option${index === this.active ? ' lib-combobox-option-active' : ''}`;
      return html`<li id=${optionId} role="option" class=${classes}
        aria-selected=${String(index === selected)} @click=${() => this.select(index)}><span class="lib-combobox-option-text">${item.text}</span></li>`;
    });
    const label = this.headline ? html`<label class="lib-combobox-label" for=${id} id=${labelId}><span class="lib-combobox-label-text">${this.headline}</span><slot name="help"></slot></label>` : nothing;
    const list = this.open ? html`<ul popover="manual" id=${listboxId} class="lib-combobox-listbox" role="listbox" aria-activedescendant=${activeId} tabindex="-1"
      aria-labelledby=${listLabel} style=${listStyle}>${options}</ul>` : nothing;
    const message = this.message ? html`<div id=${messageId} class="lib-combobox-message">${this.message}</div>` : nothing;
    return html`<slot hidden @slotchange=${this.syncOptions}></slot><div class=${this.controlClasses} @keydown=${this.key}>
      ${label}
      <div class="lib-combobox-anchor">
        <button type="button" id=${id} class="lib-combobox-field" role="combobox" aria-haspopup="listbox"
          aria-haspopup="listbox" aria-expanded=${String(this.open)} aria-controls=${listboxId}
          aria-activedescendant=${activeId}
          aria-label=${this.ariaLabel || nothing} aria-labelledby=${fieldLabel}
          aria-invalid=${this.error ? 'true' : nothing} aria-describedby=${this.message ? messageId : nothing}
          ?disabled=${this.effectivelyDisabled} @click=${() => this.open ? this.close() : this.show()}>
          <span class=${valueClass}>${hasValue ? this.items[selected].text : this.placeholder}</span>
          <span class="lib-combobox-chevron" aria-hidden="true"><lib-icon icon-url="_content/WAGO.SolutionsPlatform.UILibrary.Core/wago-icons-svg/btn-chevron-down.svg"></lib-icon></span>
        </button>
        ${list}
      </div>
      ${message}
    </div>`;
  }

  override render() {
    if (!this.searchable) return this.renderSelectionOnly();
    const id = this.inputId || this.idPrefix, selected = this.items.findIndex(item => item.value === this.value);
    const listboxId = `${id}-listbox`;
    const messageId = `${id}-message`;
    const fieldLabel = this.headline ? `${id}-label` : nothing;
    const listLabel = this.headline ? `${id}-label` : id;
    const listStyle = `max-height: ${this.maxHeight}px`;
    const labelId = `${id}-label`;
    const visible = this.visible;
    const activeIndex = visible[this.active]?.index;
    const activeId = this.open && activeIndex !== undefined ? `${id}-option-${activeIndex}` : nothing;
    const options = visible.map(({ item, index }, visibleIndex) => {
      const optionId = `${id}-option-${index}`;
      const classes = `lib-combobox-option${visibleIndex === this.active ? ' lib-combobox-option-active' : ''}`;
      return html`<li id=${optionId} role="option" class=${classes}
        aria-selected=${String(index === selected)} @pointerdown=${(event: PointerEvent) => event.preventDefault()} @click=${() => this.select(index)}><span class="lib-combobox-option-text">${item.text}</span></li>`;
    });
    const label = this.headline ? html`<label class="lib-combobox-label" for=${id} id=${labelId}><span class="lib-combobox-label-text">${this.headline}</span><slot name="help"></slot></label>` : nothing;
    const noResults = !visible.length && this.noResults
      ? html`<li role="presentation" class="lib-combobox-no-results">${this.noResults}</li>` : nothing;
    const list = this.open ? html`<ul popover="manual" id=${listboxId} class="lib-combobox-listbox" role="listbox" aria-activedescendant=${activeId} tabindex="-1"
      aria-labelledby=${listLabel} style=${listStyle}>${options}${noResults}</ul>` : nothing;
    const message = this.message ? html`<div id=${messageId} class="lib-combobox-message">${this.message}</div>` : nothing;
    return html`<slot hidden @slotchange=${this.syncOptions}></slot><div class=${this.controlClasses} @keydown=${this.key}>
      ${label}
      <div class="lib-combobox-anchor">
        <div class="lib-combobox-field lib-combobox-search-field" @click=${this.openSearch}>
          <input id=${id} class="lib-combobox-input" role="combobox" aria-autocomplete="list" aria-haspopup="listbox"
            .value=${live(this.editing ? this.query : this.committedText)} placeholder=${this.placeholder}
            aria-haspopup="listbox" aria-expanded=${String(this.open)} aria-controls=${listboxId} aria-activedescendant=${activeId}
            aria-label=${this.ariaLabel || nothing} aria-labelledby=${fieldLabel}
            aria-invalid=${this.error ? 'true' : nothing} aria-describedby=${this.message ? messageId : nothing}
            ?disabled=${this.effectivelyDisabled} autocomplete="off"
            @focus=${(event: FocusEvent) => (event.target as HTMLInputElement).select()}
            @input=${this.input}
            @change=${(event: Event) => event.stopPropagation()} @blur=${() => this.close()}>
          <span class="lib-combobox-chevron" aria-hidden="true"><lib-icon icon-url="_content/WAGO.SolutionsPlatform.UILibrary.Core/wago-icons-svg/btn-chevron-down.svg"></lib-icon></span>
        </div>
        ${list}
      </div>
      ${message}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'lib-combobox': LibComboBox; }
}
